/**
 * WorldEventPanel — Modal panel that presents a world event to the player.
 * Shows the event description and choice buttons.
 * On choice: applies outcome and shows a result toast.
 *
 * Listens for EventBus 'worldEvent:fired' and auto-opens for the next queued event.
 *
 * @module WorldEventPanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import Toast from './Toast.js';

const CATEGORY_LABELS = {
  travel:    'Travel Event',
  discovery: 'Discovery',
  encounter: 'Encounter',
  crisis:    'Crisis',
};

const CATEGORY_COLORS = {
  travel:    '#8aaa44',
  discovery: '#4a9acc',
  encounter: '#c8a020',
  crisis:    '#c83020',
};

class WorldEventPanel extends UIPanel {
  constructor() {
    super('panel-world-event', { trapFocus: true, zLayer: 'modal' });

    this._currentEventId = null;
    this._queue = []; // Events waiting to be shown

    this._applyStyles();
    this._bindBusEvents();

    DebugLog.info('[ui] WorldEventPanel created');
  }

  // ─── Styles ───────────────────────────────────────────────────────────────

  _applyStyles() {
    Object.assign(this.el.style, {
      position:  'fixed',
      top:       '50%',
      left:      '50%',
      transform: 'translate(-50%, -50%)',
      width:     'clamp(320px, 42vw, 580px)',
      maxHeight: '80vh',
      overflowY: 'auto',
      background: 'rgba(10, 6, 2, 0.97)',
      border:    '1px solid #5a4a2a',
      borderRadius: '6px',
      padding:   '0',
      color:     '#ddccaa',
      fontFamily: 'serif',
      boxShadow: '0 8px 40px rgba(0,0,0,0.8)',
      display:   'none',
    });
  }

  // ─── EventBus integration ─────────────────────────────────────────────────

  _bindBusEvents() {
    EventBus.on('worldEvent:fired', ({ event }) => {
      this._queue.push(event);
      if (!this._visible) {
        this._showNext();
      }
    });

    EventBus.on('worldEvent:resolved', () => {
      // After resolution, show next queued event if any
      if (this._queue.length > 0) {
        setTimeout(() => this._showNext(), 300);
      }
    });
  }

  // ─── Show ────────────────────────────────────────────────────────────────

  _showNext() {
    if (this._queue.length === 0) return;
    const event = this._queue.shift();
    this._currentEventId = event.id;
    this._render(event);
    this.show();
  }

  /**
   * Open with a specific event object.
   * @param {object} event
   */
  openEvent(event) {
    this._currentEventId = event.id;
    this._render(event);
    this.show();
  }

  show(data) {
    this.el.style.display = 'block';
    super.show(data);
  }

  hide() {
    this.el.style.display = 'none';
    super.hide();
    this._currentEventId = null;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  _render(event) {
    const catLabel = CATEGORY_LABELS[event.category] ?? event.category;
    const catColor = CATEGORY_COLORS[event.category] ?? '#ddccaa';

    const choicesHTML = (event.choices ?? []).map(choice => `
      <button
        class="sr-event-choice-btn"
        data-choice-id="${choice.id}"
        style="
          display: block;
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid #5a4a2a;
          border-radius: 4px;
          color: #ddccaa;
          font-family: serif;
          font-size: clamp(12px, 1.3vw, 14px);
          padding: 10px 14px;
          cursor: pointer;
          text-align: left;
          margin-bottom: 8px;
          transition: background 0.15s, border-color 0.15s;
          min-height: 44px;
        "
      >${choice.text}</button>
    `).join('');

    this.el.innerHTML = `
      <div style="
        background: rgba(${catColor === '#c83020' ? '80,10,5' : '30,20,5'},0.6);
        border-bottom: 1px solid #3a2a0a;
        padding: 12px 16px 10px;
        display: flex;
        align-items: center;
        gap: 10px;
      ">
        <span style="
          font-size: clamp(9px, 1vw, 11px);
          font-weight: bold;
          letter-spacing: 0.08em;
          color: ${catColor};
          text-transform: uppercase;
          border: 1px solid ${catColor};
          border-radius: 3px;
          padding: 2px 7px;
          flex-shrink: 0;
        ">${catLabel}</span>
        <span style="
          font-size: clamp(14px, 1.6vw, 18px);
          font-weight: bold;
          color: #e8d8a0;
          flex: 1;
        ">${event.name}</span>
      </div>
      <div style="padding: 16px;">
        <p style="
          font-size: clamp(12px, 1.3vw, 14px);
          line-height: 1.6;
          color: #bbaa88;
          margin: 0 0 18px 0;
        ">${event.description}</p>
        <div class="sr-event-choices">
          ${choicesHTML}
        </div>
      </div>
    `;

    // Bind choice buttons
    this.el.querySelectorAll('.sr-event-choice-btn').forEach(btn => {
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'rgba(255,255,255,0.09)';
        btn.style.borderColor = '#8a6a2a';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'rgba(255,255,255,0.04)';
        btn.style.borderColor = '#5a4a2a';
      });
      btn.addEventListener('click', () => {
        const choiceId = btn.dataset.choiceId;
        this._makeChoice(choiceId);
      });
    });
  }

  // ─── Choice resolution ────────────────────────────────────────────────────

  _makeChoice(choiceId) {
    const eventId = this._currentEventId;
    if (!eventId) return;

    const wes = window.__worldEventSystem;
    if (!wes) {
      DebugLog.error('[ui] WorldEventPanel: WorldEventSystem not found on window');
      this.hide();
      return;
    }

    const result = wes.resolveEvent(eventId, choiceId);

    this.hide();

    if (result.ok) {
      Toast.show(result.message, {
        type: result.outcome?.type === 'trigger_combat' ? 'error'
            : result.outcome?.type === 'nothing' ? 'info'
            : result.outcome?.fame_delta > 0 ? 'success'
            : 'info',
        duration: 3000,
      });
    } else {
      Toast.show(result.message, { type: 'error', duration: 3000 });
      DebugLog.warn(`[ui] WorldEventPanel resolveEvent failed: ${result.message}`);
    }
  }
}

export default WorldEventPanel;
